// Shares and placements (Sharing.swift, docs/43). A share (`shares/{id}`) is written only by the server's `share`
// function, so the apps only read it. A placement (`users/{uid}/placements/{shareID}`) holds what is the person's
// own about a share of someone else: where the shared list shows, their plan for its to-dos, the sections they
// collapsed.

import { normalizedUUID } from "../ids";
import { readRecord } from "./shared";
import {
  decoding, document, readMap, readStruct, readBool, withoutUndefined, storedFields,
  type DecodeContext, type Decoded, type EncodeOptions, type Fields, type ValueReader,
} from "./values";

// MARK: - Share

export interface ShareMember {
  name: string;
  email: string;
  /** "owner" or "member". */
  role: string;
}

export interface ShareInvitee {
  email: string;
  name?: string;
}

export interface Share {
  /** The document's ID. */
  id: string;
  /** "list", "project" or "area"; a string, so a kind from a newer version is read rather than refused. */
  kind: string;
  /** The shared list, project or area. */
  recordID: string;
  title: string;
  ownerUID: string;
  memberUIDs: string[];
  members: Record<string, ShareMember>;
  /** Invitations still waiting, by their id. */
  invited?: Record<string, ShareInvitee>;
}

const readMember: ValueReader<ShareMember> = readStruct((fields) => ({
  name: fields.string("name"), email: fields.string("email"), role: fields.string("role"),
}));

const readInvitee: ValueReader<ShareInvitee> = readStruct((fields) => withoutUndefined<ShareInvitee>({
  email: fields.string("email"), name: fields.optionalString("name"),
}));

/** `shares/{id}` as stored (StoredShare): a share whose record isn't a UUID is no share. */
export function decodeShare(id: string, data: Fields, context: DecodeContext): Decoded<Share> {
  return decoding(() => readRecord(data, context, (fields) => {
    const kind = fields.string("kind");
    const record = fields.string("recordID");
    const title = fields.optionalString("title");
    const ownerUID = fields.string("ownerUID");
    const memberUIDs = fields.strings("memberUIDs");
    const members = fields.optional("members", readMap(readMember));
    const invited = fields.optional("invited", readMap(readInvitee));
    const recordID = normalizedUUID(record);
    if (!recordID) throw new Error("recordID is not a UUID");
    return withoutUndefined<Share>({ id, kind, recordID, title: title ?? "", ownerUID, memberUIDs, members: members ?? {}, invited });
  }));
}

export function isAreaShare(share: Share): boolean {
  return share.kind === "area";
}

// MARK: - Placement

/** One to-do's plan in a share of someone else: Today's section, the planned time, the order. */
export interface TodoPlan {
  todaySectionID?: string;
  plannedUntil?: Date;
  planOrder?: number;
}

/** Every field optional: older versions wrote only `areaID`. */
export interface Placement {
  areaID?: string;
  /** A list or project shared with this person: its place among their projects of that area. */
  order?: number;
  /** This person's plan for the share's to-dos, by to-do id. */
  plans?: Record<string, TodoPlan>;
  /** The share's sections this person collapsed, by section id. */
  collapsed?: Record<string, boolean>;
}

/** The fields of a record in a share of someone else that are this person's own: never written into the share. */
export const PERSONAL_TODO_FIELDS: readonly string[] = ["todaySectionID", "plannedUntil", "planOrder"];
export const PERSONAL_SECTION_FIELDS: readonly string[] = ["isCollapsed"];
/** For a list or project shared on its own (in a shared area, the area and the order are the owner's). */
export const PERSONAL_PROJECT_FIELDS: readonly string[] = ["areaID", "order"];

const readTodoPlan: ValueReader<TodoPlan> = readStruct((fields) => withoutUndefined<TodoPlan>({
  todaySectionID: fields.optionalUUID("todaySectionID"),
  plannedUntil: fields.optionalMoment("plannedUntil"),
  planOrder: fields.optionalNumber("planOrder"),
}));

/**
 * A placement as the kit reads it: the whole of it, or, when that can't be read, only its `areaID` (what older
 * versions wrote). Never fails.
 */
export function decodePlacement(data: Fields, context: DecodeContext): Placement {
  const whole = decoding(() => readRecord(data, context, (fields) => withoutUndefined<Placement>({
    areaID: fields.optionalUUID("areaID"),
    order: fields.optionalNumber("order"),
    plans: fields.optional("plans", readMap(readTodoPlan)),
    collapsed: fields.optional("collapsed", readMap(readBool)),
  })));
  if (whole.ok) return whole.value;
  const areaID = normalizedUUID(data.areaID);
  return areaID ? { areaID } : {};
}

/**
 * The fields a placement writes, merged field by field into its document (FirestoreBackend.setPlacement, with
 * `modified` added there). Only the fields it has: one that became absent is not written, so it stays as it was.
 */
export function encodePlacement(placement: Placement, options?: EncodeOptions): Fields {
  const plans = placement.plans && Object.fromEntries(Object.entries(placement.plans).map(([id, plan]) => [id, document({}, {
    todaySectionID: plan.todaySectionID, plannedUntil: plan.plannedUntil, planOrder: plan.planOrder,
  })]));
  return storedFields(document({}, {
    areaID: placement.areaID, order: placement.order, plans, collapsed: placement.collapsed && { ...placement.collapsed },
  }), options);
}
